import { useEffect, useId, useRef, useState } from "react";
import { PHONE_QUERY, useMediaQuery } from "../lib/useMediaQuery";
import { Modal } from "./Modal";
import { useLocation, useNavigate } from "react-router";
import { buildingIdFromPath, useBuildingContext } from "../lib/buildingContext";
import { useClientContext } from "../lib/clientContext";
import { IconCheck, IconChevronDown, IconClient, IconSearch } from "./icons";

/** Sneltoets K opent de klantwisselaar, behalve tijdens typen. */
const typing = (el: EventTarget | null) =>
  el instanceof HTMLElement && (el.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(el.tagName));

/**
 * Klantwisselaar in de bovenbalk (docs/CLIENTS.md §11): kies de klant waarin je werkt, of "Alle klanten". De
 * gebouwkiezer en de lijsten volgen de keuze. Wie maar één klant met gebouwen ziet, krijgt geen wisselaar.
 */
export function ClientSwitcher() {
  const { id, client, clients, setClient } = useClientContext();
  const { building, setBuilding } = useBuildingContext();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef<HTMLDivElement>(null);
  const btn = useRef<HTMLButtonElement>(null);
  const popId = useId();
  // Telefoon: dezelfde sheet van onderen als de verdieping- en ruimtekiezer (UX-RICHTLIJNEN §10).
  const phone = useMediaQuery(PHONE_QUERY);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const list = (clients ?? []).filter((c) => c.buildingCount > 0 || c.id === id);
  const show = list.length > 1 || Boolean(client);

  useEffect(() => setOpen(false), [pathname]);
  useEffect(() => {
    if (!show) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() !== "k" || e.ctrlKey || e.metaKey || e.altKey || typing(e.target)) return;
      if (document.querySelector(".modalbg")) return;
      e.preventDefault();
      setOpen(true);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [show]);
  useEffect(() => {
    if (!open) return;
    setQ("");
    const onDown = (e: PointerEvent) => {
      if (phone) return;
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      setOpen(false);
      btn.current?.focus();
    };
    document.addEventListener("pointerdown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, phone]);

  if (!show) return null;

  function choose(next: string | null) {
    setOpen(false);
    btn.current?.focus();
    setClient(next);
    // Een gekozen gebouw van een andere klant laat je los; op een gebouwpagina ga je naar de gebouwenlijst
    // (ook als de gebouwen nog laden, anders blijft een gebouw van een andere klant staan).
    if (next && building?.clientId !== next) {
      if (building) setBuilding(null);
      if (buildingIdFromPath(pathname)) navigate("/");
    }
  }

  const t = q.trim().toLowerCase();
  const shown = list.filter(
    (c) => !t || [c.name, c.shortName, c.city].filter(Boolean).join(" ").toLowerCase().includes(t),
  );
  const current = client ? client.name : "Alle klanten";

  return (
    <div className="bswitch cswitch" ref={ref}>
      <button
        ref={btn}
        type="button"
        className="bswitch-btn"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? popId : undefined}
        aria-label={`Klant: ${current}. Andere klant kiezen`}
        aria-keyshortcuts="K"
        title="Klant kiezen (K)"
        onClick={() => setOpen(!open)}
      >
        <IconClient size={20} aria-hidden="true" />
        <span className="bswitch-label">
          {client ? (
            (client.shortName ?? client.name)
          ) : (
            <>
              Alle<span className="bswitch-more"> klanten</span>
            </>
          )}
        </span>
        <IconChevronDown size={18} aria-hidden="true" />
      </button>
      {open && phone && (
        <Modal id={popId} title="Klant kiezen" onClose={() => setOpen(false)}>
          <div className="bswitch-sheet">
            {list.length > 6 && (
              <label className="bswitch-search">
                <IconSearch size={18} aria-hidden="true" />
                <input
                  type="search"
                  autoComplete="off"
                  autoFocus
                  placeholder="Zoek op naam of plaats"
                  aria-label="Klant zoeken"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                />
              </label>
            )}
            <div className="bswitch-list">
              <button
                type="button"
                className="menuitem"
                aria-current={!id}
                autoFocus={list.length <= 6}
                onClick={() => choose(null)}
              >
                {!id ? <IconCheck size={20} aria-hidden="true" /> : <span className="ico-space" aria-hidden="true" />}
                <strong className="grow">Alle klanten</strong>
              </button>
              {shown.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  className="menuitem"
                  aria-current={c.id === id}
                  onClick={() => choose(c.id)}
                >
                  {c.id === id ? (
                    <IconCheck size={20} aria-hidden="true" />
                  ) : (
                    <span className="ico-space" aria-hidden="true" />
                  )}
                  <span className="grow">
                    {c.isUnassigned ? <em>{c.name}</em> : c.name}
                    {c.city && <span className="muted small block">{c.city}</span>}
                  </span>
                  <span className="muted small">{c.buildingCount}</span>
                </button>
              ))}
              {t && !shown.length && <p className="muted small bswitch-none">Geen klant gevonden.</p>}
            </div>
          </div>
        </Modal>
      )}
      {open && !phone && (
        <div className="popover bswitch-pop" id={popId} role="dialog" aria-label="Klant kiezen">
          {list.length > 6 && (
            <label className="bswitch-search">
              <IconSearch size={18} aria-hidden="true" />
              <input
                type="search"
                autoComplete="off"
                autoFocus
                placeholder="Zoek op naam of plaats"
                aria-label="Klant zoeken"
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
            </label>
          )}
          <div className="bswitch-list">
            <button
              type="button"
              className="menuitem"
              aria-current={!id}
              autoFocus={list.length <= 6}
              onClick={() => choose(null)}
            >
              {!id ? <IconCheck size={20} aria-hidden="true" /> : <span className="ico-space" aria-hidden="true" />}
              <strong className="grow">Alle klanten</strong>
            </button>
            {shown.map((c) => (
              <button
                key={c.id}
                type="button"
                className="menuitem"
                aria-current={c.id === id}
                onClick={() => choose(c.id)}
              >
                {c.id === id ? (
                  <IconCheck size={20} aria-hidden="true" />
                ) : (
                  <span className="ico-space" aria-hidden="true" />
                )}
                <span className="grow">
                  {c.isUnassigned ? <em>{c.name}</em> : c.name}
                  {c.city && <span className="muted small block">{c.city}</span>}
                </span>
                <span className="muted small">{c.buildingCount}</span>
              </button>
            ))}
            {t && !shown.length && <p className="muted small bswitch-none">Geen klant gevonden.</p>}
          </div>
        </div>
      )}
    </div>
  );
}
